import React, { useState, useRef, useEffect } from 'react';

// Same difficulty scale as Adições e Subtrações.
const LEVELS = [
  { id: 1, title: 'Nível 1', description: 'Até 20', max: 20 },
  { id: 2, title: 'Nível 2', description: 'Até 100', max: 100 },
  { id: 3, title: 'Nível 3', description: 'Até 500', max: 500 },
  { id: 4, title: 'Nível 4', description: 'Até 1000', max: 1000 },
];

// Generate an equation with one of the operands hidden. The result is always
// shown — the user works backwards to recover the missing number.
//
// Returns: { a, b, op, result, missing: 'a' | 'b', answer }
const generateQuestion = (max) => {
  const isAddition = Math.random() < 0.5;

  let a, b, result;
  if (isAddition) {
    a = Math.floor(Math.random() * (max + 1));
    b = Math.floor(Math.random() * (max - a + 1));
    result = a + b;
  } else {
    a = Math.floor(Math.random() * (max + 1));
    b = Math.floor(Math.random() * (a + 1));
    result = a - b;
  }

  const op = isAddition ? '+' : '−';
  const missing = Math.random() < 0.5 ? 'a' : 'b';
  const answer = missing === 'a' ? a : b;
  return { a, b, op, result, missing, answer };
};

const MissingOperand = () => {
  const [level, setLevel] = useState(null);
  const [question, setQuestion] = useState(null);
  const [userAnswer, setUserAnswer] = useState('');
  const [feedback, setFeedback] = useState(null); // null | 'correct' | 'incorrect'
  const [score, setScore] = useState({ correct: 0, total: 0 });
  const inputRef = useRef(null);
  const nextButtonRef = useRef(null);

  // Focus the input while the user is entering an answer; once feedback is
  // shown the input is disabled (and so swallows key events), so move focus
  // to "Próxima" instead — pressing Enter then advances to the next question
  // on both correct and incorrect responses.
  useEffect(() => {
    if (feedback === null && question !== null && inputRef.current) {
      inputRef.current.focus();
    } else if (feedback !== null && nextButtonRef.current) {
      nextButtonRef.current.focus();
    }
  }, [question, feedback]);

  useEffect(() => {
    if (feedback !== 'correct') return;
    const timeoutId = setTimeout(() => nextQuestion(), 900);
    return () => clearTimeout(timeoutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [feedback]);

  const startLevel = (selected) => {
    setLevel(selected);
    setQuestion(generateQuestion(selected.max));
    setUserAnswer('');
    setFeedback(null);
    setScore({ correct: 0, total: 0 });
  };

  const checkAnswer = () => {
    if (feedback !== null || question === null) return;
    const trimmed = userAnswer.trim();
    if (trimmed === '') return;
    const parsed = Number(trimmed);
    if (!Number.isInteger(parsed)) return;

    const isCorrect = parsed === question.answer;
    setFeedback(isCorrect ? 'correct' : 'incorrect');
    setScore((s) => ({
      correct: s.correct + (isCorrect ? 1 : 0),
      total: s.total + 1,
    }));
  };

  const nextQuestion = () => {
    setQuestion(generateQuestion(level.max));
    setUserAnswer('');
    setFeedback(null);
  };

  const changeLevel = () => {
    setLevel(null);
    setQuestion(null);
    setUserAnswer('');
    setFeedback(null);
    setScore({ correct: 0, total: 0 });
  };

  const handleKeyDown = (e) => {
    if (e.key !== 'Enter') return;
    e.preventDefault();
    if (feedback === 'incorrect') {
      nextQuestion();
    } else if (feedback === null) {
      checkAnswer();
    }
  };

  if (level === null) {
    return (
      <div className="game-container">
        <h1 className="game-title">Número em Falta</h1>
        <h2 className="config-subtitle">Escolhe o nível</h2>
        <div className="game-menu">
          {LEVELS.map((l) => (
            <button
              key={l.id}
              className="menu-button"
              onClick={() => startLevel(l)}
            >
              {l.title} — {l.description}
            </button>
          ))}
        </div>
      </div>
    );
  }

  const renderOperand = (which) => {
    if (question.missing === which) {
      return (
        <input
          ref={inputRef}
          type="text"
          inputMode="numeric"
          pattern="[0-9]*"
          className={`math-answer-input ${feedback || ''}`}
          value={userAnswer}
          onChange={(e) => setUserAnswer(e.target.value.replace(/[^0-9]/g, ''))}
          onKeyDown={handleKeyDown}
          disabled={feedback !== null}
          aria-label="Número em falta"
        />
      );
    }
    return (
      <span className="math-operand">
        {which === 'a' ? question.a : question.b}
      </span>
    );
  };

  return (
    <div className="game-container">
      <h1 className="game-title">Número em Falta</h1>
      <div className="game-status">
        {level.title} ({level.description}) · Pontuação: {score.correct} / {score.total}
      </div>

      <div className="math-equation">
        {renderOperand('a')}
        <span className="math-operator">{question.op}</span>
        {renderOperand('b')}
        <span className="math-operator">=</span>
        <span className="math-operand">{question.result}</span>
      </div>

      <div className={`math-feedback ${feedback || ''}`}>
        {feedback === 'correct' && 'Correto!'}
        {feedback === 'incorrect' &&
          `Errado. ${question.a} ${question.op} ${question.b} = ${question.result}.`}
      </div>

      <div className="game-buttons">
        {feedback === null ? (
          <button className="new-game-button" onClick={checkAnswer}>
            Verificar
          </button>
        ) : (
          <button
            ref={nextButtonRef}
            className="new-game-button"
            onClick={nextQuestion}
          >
            Próxima
          </button>
        )}
        <button className="new-game-button" onClick={changeLevel}>
          Mudar Nível
        </button>
      </div>
    </div>
  );
};

export default MissingOperand;
